
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Search, RefreshCw, Edit } from 'lucide-react';
import { recoverLegacyGameData, createManualGameData, cleanupLegacyData } from '@/lib/game-data-recovery';
import type { GameData } from '@/hooks/use-game-data';

interface DataRecoveryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDataRecovered: (data: GameData) => void;
}

const DataRecoveryDialog = ({ isOpen, onClose, onDataRecovered }: DataRecoveryDialogProps) => {
  const [recoveryMode, setRecoveryMode] = useState<'auto' | 'manual'>('auto');
  const [manualData, setManualData] = useState({
    bestScore: '',
    totalGamesPlayed: '',
    currentStreak: ''
  });
  const [isRecovering, setIsRecovering] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState<string>('');

  const handleAutoRecovery = async () => {
    setIsRecovering(true);
    setRecoveryResult('');
    
    try {
      const recoveredData = recoverLegacyGameData();
      
      if (recoveredData) {
        setRecoveryResult(`Données récupérées ! Score: ${recoveredData.bestScore}, Parties: ${recoveredData.totalGamesPlayed}`);
        onDataRecovered(recoveredData);
        cleanupLegacyData();
        setTimeout(() => onClose(), 2000);
      } else {
        setRecoveryResult('Aucune donnée récupérable trouvée dans le stockage local.');
      }
    } catch (error) {
      setRecoveryResult('Erreur lors de la récupération automatique.');
      console.error('Recovery error:', error);
    } finally {
      setIsRecovering(false);
    }
  };

  const handleManualRecovery = () => {
    const bestScore = parseInt(manualData.bestScore) || 0;
    const totalGamesPlayed = parseInt(manualData.totalGamesPlayed) || 0;
    const currentStreak = parseInt(manualData.currentStreak) || 0;

    if (bestScore === 0 && totalGamesPlayed === 0) {
      setRecoveryResult('Veuillez entrer au moins un score ou un nombre de parties.');
      return;
    }

    const gameData = createManualGameData(bestScore, totalGamesPlayed, currentStreak);
    onDataRecovered(gameData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw size={20} />
            Récupération des données
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <Button
              variant={recoveryMode === 'auto' ? 'default' : 'outline'}
              onClick={() => setRecoveryMode('auto')}
              className="flex-1"
            >
              <Search size={16} className="mr-1" />
              Auto
            </Button>
            <Button
              variant={recoveryMode === 'manual' ? 'default' : 'outline'}
              onClick={() => setRecoveryMode('manual')}
              className="flex-1"
            >
              <Edit size={16} className="mr-1" />
              Manuel
            </Button>
          </div>

          {recoveryMode === 'auto' ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Recherche automatique de vos anciennes données de jeu dans le stockage local du navigateur.
              </p>
              
              <Button 
                onClick={handleAutoRecovery}
                disabled={isRecovering}
                className="w-full"
              >
                {isRecovering ? (
                  <>
                    <RefreshCw size={16} className="mr-2 animate-spin" />
                    Recherche...
                  </>
                ) : (
                  <>
                    <Search size={16} className="mr-2" />
                    Rechercher mes données
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Si vous vous souvenez de vos statistiques, entrez-les manuellement :
              </p>
              
              <div className="space-y-2">
                <div>
                  <Label htmlFor="bestScore">Meilleur score</Label>
                  <Input
                    id="bestScore"
                    type="number"
                    placeholder="0"
                    value={manualData.bestScore}
                    onChange={(e) => setManualData(prev => ({ ...prev, bestScore: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label htmlFor="totalGames">Nombre de parties jouées</Label>
                  <Input
                    id="totalGames"
                    type="number"
                    placeholder="0"
                    value={manualData.totalGamesPlayed}
                    onChange={(e) => setManualData(prev => ({ ...prev, totalGamesPlayed: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label htmlFor="streak">Série actuelle (optionnel)</Label>
                  <Input
                    id="streak"
                    type="number"
                    placeholder="0"
                    value={manualData.currentStreak}
                    onChange={(e) => setManualData(prev => ({ ...prev, currentStreak: e.target.value }))}
                  />
                </div>
              </div>
              
              <Button onClick={handleManualRecovery} className="w-full">
                Restaurer ces données
              </Button>
            </div>
          )}

          {recoveryResult && (
            <Alert>
              <AlertDescription>{recoveryResult}</AlertDescription>
            </Alert>
          )}

          <Button variant="outline" onClick={onClose} className="w-full">
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DataRecoveryDialog;
